import { createSelector, createSlice } from '@reduxjs/toolkit';

export const MAX_CONTEXTS = 3;

const buildKey = (type, uri) => `${type}:${uri}`;

const initialState = {
    items: [],
    currentPage: {
        title: null,
        pathname: null,
        suggestions: null,
        suggestionsStatus: 'idle',
    },
};

const chatContextSlice = createSlice({
    name: 'chatContext',
    initialState,
    reducers: {
        pageMetaSet(state, action) {
            const payload = action.payload || {};
            state.currentPage = { ...state.currentPage, ...payload };
        },
        pageMetaCleared(state) {
            state.currentPage = { title: null, pathname: null, suggestions: null, suggestionsStatus: 'idle' };
        },
        contextAdded(state, action) {
            const { type, uri, label, link, source = 'programmatic' } = action.payload || {};
            if (!type || !uri) return;
            const key = buildKey(type, uri);
            const existingIdx = state.items.findIndex((c) => c.key === key);
            if (existingIdx >= 0) {
                const prev = state.items[existingIdx];
                state.items[existingIdx] = {
                    ...prev,
                    label: label ?? prev.label,
                    link: link ?? prev.link,
                    source: prev.source === 'auto' ? prev.source : source,
                };
                return;
            }
            if (state.items.length >= MAX_CONTEXTS) return;
            state.items.push({
                key,
                type,
                uri,
                label: label ?? null,
                link: link || null,
                source,
                addedAt: Date.now(),
            });
        },
        contextRemoved(state, action) {
            const key = action.payload;
            state.items = state.items.filter((c) => c.key !== key);
        },
        contextRemovedByTypeUri(state, action) {
            const { type, uri } = action.payload || {};
            const key = buildKey(type, uri);
            state.items = state.items.filter((c) => c.key !== key);
        },
        contextsCleared() {
            return initialState;
        },
        contextUpdated(state, action) {
            const { key, patch } = action.payload || {};
            const idx = state.items.findIndex((c) => c.key === key);
            if (idx < 0) return;
            const prev = state.items[idx];
            state.items[idx] = {
                ...prev,
                ...(patch?.label !== undefined && patch.label !== null ? { label: patch.label } : {}),
                ...(patch?.link !== undefined && patch.link !== null ? { link: patch.link } : {}),
            };
        },
    },
});

export const {
    contextAdded,
    contextRemoved,
    contextRemovedByTypeUri,
    contextsCleared,
    contextUpdated,
    pageMetaSet,
    pageMetaCleared,
} = chatContextSlice.actions;

export default chatContextSlice.reducer;

const selectRoot = (state) => state.chatContext;
export const selectContexts = createSelector(selectRoot, (root) => root.items);
export const selectContextCount = createSelector(selectContexts, (items) => items.length);
export const selectSendPayloadContexts = createSelector(selectContexts, (items) =>
    items.map((c) => ({ type: c.type, uri: c.uri })),
);
export const selectCurrentPageMeta = createSelector(selectRoot, (root) => root.currentPage);
export const selectHasContext = (type, uri) => (state) => {
    const key = buildKey(type, uri);
    return state.chatContext.items.some((c) => c.key === key);
};
